import type { components } from '@/types/apiSchema';

/** The consent request and a saved connection both name their client this way. */
export type Client = Pick<
  components['schemas']['OAuthConsentResponse'],
  'clientName' | 'redirectHost'
>;

interface ClientBrand {
  name: string;
  /** Redirect hosts this app owns; subdomains match too. */
  hosts: string[];
  /** Matched against `clientName` only for loopback redirects. */
  namePattern: RegExp;
  tileClassName: string;
  /** Mark from Simple Icons (CC0), served from `public/` so it stays out of the bundle. */
  logo: string;
}

const clientBrands: ClientBrand[] = [
  {
    name: 'ChatGPT',
    hosts: ['chatgpt.com', 'chat.openai.com', 'openai.com'],
    namePattern: /chatgpt|openai/i,
    tileClassName: 'bg-white',
    logo: '/mcp-clients/chatgpt.svg',
  },
  {
    name: 'Claude',
    hosts: ['claude.ai', 'claude.com', 'anthropic.com'],
    namePattern: /claude|anthropic/i,
    tileClassName: 'bg-[#F4EFE6]',
    logo: '/mcp-clients/claude.svg',
  },
  {
    name: 'Gemini',
    hosts: ['gemini.google.com'],
    namePattern: /gemini/i,
    tileClassName: 'bg-white',
    logo: '/mcp-clients/gemini.svg',
  },
  {
    name: 'Perplexity',
    hosts: ['perplexity.ai'],
    namePattern: /perplexity/i,
    tileClassName: 'bg-[#091717]',
    logo: '/mcp-clients/perplexity.svg',
  },
  {
    name: 'Le Chat',
    hosts: ['mistral.ai'],
    namePattern: /le chat|mistral/i,
    tileClassName: 'bg-white',
    logo: '/mcp-clients/le-chat.svg',
  },
  {
    name: 'GitHub Copilot',
    hosts: ['vscode.dev', 'github.com'],
    namePattern: /copilot|visual studio code|vs ?code/i,
    tileClassName: 'bg-[#0D1117]',
    logo: '/mcp-clients/github-copilot.svg',
  },
  {
    name: 'Cursor',
    hosts: ['cursor.com', 'cursor.sh'],
    namePattern: /cursor/i,
    tileClassName: 'bg-[#0A0A0A]',
    logo: '/mcp-clients/cursor.svg',
  },
  {
    name: 'Windsurf',
    hosts: ['windsurf.com', 'codeium.com'],
    namePattern: /windsurf|codeium/i,
    tileClassName: 'bg-[#0B100F]',
    logo: '/mcp-clients/windsurf.svg',
  },
];

const loopbackHosts = new Set(['localhost', '127.0.0.1', '[::1]']);

const hostname = (redirectHost: string) => {
  try {
    return new URL(`https://${redirectHost}`).hostname;
  } catch {
    return redirectHost.toLowerCase();
  }
};

/**
 * The client's brand, picked from `redirectHost` because the client chooses
 * its own name: an app calling itself "Claude" that redirects elsewhere gets
 * no Claude logo. Loopback redirects only reach the user's own machine, so
 * local apps (Claude Code, Cursor) are matched by name. No brand means
 * Finlay can't verify the client, so the consent page names its host instead.
 */
export const findClientBrand = ({ clientName, redirectHost }: Client) => {
  const host = hostname(redirectHost);
  if (loopbackHosts.has(host)) {
    return clientBrands.find((brand) => brand.namePattern.test(clientName));
  }
  return clientBrands.find((brand) =>
    brand.hosts.some((owned) => host === owned || host.endsWith(`.${owned}`)),
  );
};
